import type {ReactRef} from 'src/types/react';

import * as React from 'react';
import invariant from 'invariant';

import classify from 'src/utils/classify';
import {getAnchorPosition, type Placement} from 'src/utils/dom';

import css from './context-menu.css';

// @ts-expect-error - TS7034 - Variable 'menuRoot' implicitly has type 'any' in some locations where its type cannot be determined.
let menuRoot = null;
const clickedInsideMap = new WeakMap();

type AnchorPosition = 'start' | 'center' | 'end';

type ContextMenuProps = ContextMenuConfig & {
  children: React.ReactNode;
  menuContent?: React.ReactNode;
  anchorPosition?: AnchorPosition;
};

export default function ContextMenu(
  props: ContextMenuProps,
): React.ReactElement<React.ComponentProps<'div'>> {
  const {children, ...restProps} = props;
  const {className, anchor, onClick, onOpen, onClose} = props;
  const config = {
    anchorPosition: 'end',
    closeOnMenuClick: true,
    // @ts-expect-error - TS2783 - 'menuContent' is specified more than once, so this usage will be overwritten.
    menuContent: children,
    ...restProps,
  } as const;
  const [componentRef, handleOpen, handleClose, isOpen] =
    useContextMenu(config);

  return (
    <div
      className={classify(css.button, className)}
      // @ts-expect-error - TS2322 - Type 'ReactRef<HTMLElement>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
      ref={componentRef}
      onClick={(event) => {
        event.preventDefault();
        if (isOpen()) {
          handleClose();
          if (onClose) {
            onClose();
          }
        } else {
          handleOpen();
          if (onOpen) {
            onOpen();
          }
        }
        if (onClick) {
          onClick(event);
        }
      }}
    >
      {anchor ? anchor : '+'}
    </div>
  );
}

export type ContextMenuConfig = {
  className?: string;
  anchor?: React.ReactNode;
  noStyle?: boolean;
  contextMenuClassName?: string;
  header?: string;
  anchorPosition?: AnchorPosition;
  justify?: AnchorPosition;
  placement?: Placement;
  onClick?: (event: React.MouseEvent<HTMLElement>) => unknown;
  onExit?: () => unknown;
  onOpen?: () => unknown;
  onClose?: () => unknown;
  closeOnMenuClick?: unknown;
  disabled?: unknown;
  menuContent: React.ReactNode;
  noArrow?: unknown;
  upward?: boolean;
  downward?: boolean;
};

export type ContextMenuTuple = [
  ReactRef<HTMLElement>,
  () => void,
  () => void,
  () => boolean,
  () => void,
];

export function useContextMenu({
  onClose,
  disabled,
  closeOnMenuClick = true,
  ...config
}: ContextMenuConfig): ContextMenuTuple {
  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const componentRef = React.useRef<HTMLElement | null | undefined>();

  const handleOpen = () => {
    if (!disabled) {
      invariant(
        // @ts-expect-error - TS7005 - Variable 'menuRoot' implicitly has an 'any' type.
        menuRoot,
        'ContextMenu: Your context menu will not work without a mounted ContextMenuRoot.',
      );
      menuRoot.set(componentRef, {closeOnMenuClick, ...config, onClose});
    }
  };

  const updateMenuContent = () => {
    invariant(
      // @ts-expect-error - TS7005 - Variable 'menuRoot' implicitly has an 'any' type.
      menuRoot,
      'ContextMenu: Your context menu will not work without a mounted ContextMenuRoot.',
    );
    menuRoot.set(componentRef, {closeOnMenuClick, ...config, onClose});
  };

  const handleClose = () => {
    invariant(
      // @ts-expect-error - TS7005 - Variable 'menuRoot' implicitly has an 'any' type.
      menuRoot,
      'ContextMenu: Your context menu will not work without a mounted ContextMenuRoot.',
    );
    menuRoot.unset(componentRef);
    if (onClose) {
      onClose();
    }
  };

  // @ts-expect-error - TS7005 - Variable 'menuRoot' implicitly has an 'any' type.
  const isOpen = () => Boolean(menuRoot?.state.activeMenu === componentRef);

  // If the parent component of the menu unmounts, we remove it.
  React.useEffect(() => {
    return () => {
      window.addEventListener(
        'click',
        () => {
          // @ts-expect-error - TS7005 - Variable 'menuRoot' implicitly has an 'any' type.
          if (menuRoot && menuRoot.state.activeMenu === componentRef) {
            menuRoot.unset(componentRef);
          }
        },
        {once: true},
      );
    };
  }, []);

  return [componentRef, handleOpen, handleClose, isOpen, updateMenuContent];
}

type ContextMenuRef = {
  current: HTMLElement | null | undefined;
};
type ContextMenuRootState = {
  activeMenu: ContextMenuRef | null | undefined;
  config: ContextMenuConfig | null | undefined;
};
export class ContextMenuRoot extends React.PureComponent<
  Record<any, any>,
  ContextMenuRootState
> {
  menuElement: HTMLElement | null | undefined = null;

  // @ts-expect-error - TS4114 - This member must have an 'override' modifier because it overrides a member in the base class 'PureComponent<Record<any, any>, ContextMenuRootState, any>'.
  state: ContextMenuRootState = {
    activeMenu: null,
    config: null,
  };

  // @ts-expect-error - TS4114 - This member must have an 'override' modifier because it overrides a member in the base class 'PureComponent<Record<any, any>, ContextMenuRootState, any>'.
  componentDidMount() {
    menuRoot = this;
  }

  // @ts-expect-error - TS4114 - This member must have an 'override' modifier because it overrides a member in the base class 'PureComponent<Record<any, any>, ContextMenuRootState, any>'.
  componentWillUnmount() {
    menuRoot = null;
    this.stopListening();
  }

  // @ts-expect-error - TS4114 - This member must have an 'override' modifier because it overrides a member in the base class 'PureComponent<Record<any, any>, ContextMenuRootState, any>'.
  componentDidUpdate(_: Record<any, any>, prevState: ContextMenuRootState) {
    if (!prevState.activeMenu && this.state.activeMenu) {
      setTimeout(() => {
        window.addEventListener('click', this.handleWindowClick);
      }, 0);
    } else if (prevState.activeMenu && !this.state.activeMenu) {
      this.stopListening();
    }

    // NOTE (kyle): if the menu overlows past the bottom of the fold,
    // we pop it upward here.
    const {activeMenu, config} = this.state;
    if (activeMenu && config) {
      const menuElement = this.menuElement;
      if (menuElement) {
        const rect = menuElement.getBoundingClientRect();
        if (
          activeMenu.current &&
          (config.upward || rect.bottom > window.innerHeight) &&
          !config.downward
        ) {
          const {y} = getAnchorPosition(
            activeMenu.current,
            'top',
            0,
            config.justify,
          );

          menuElement.style.top = y - rect.height + 'px';
        }
      }
    }
  }

  stopListening() {
    window.removeEventListener('click', this.handleWindowClick);
  }

  // @ts-expect-error - TS4114 - This member must have an 'override' modifier because it overrides a member in the base class 'PureComponent<Record<any, any>, ContextMenuRootState, any>'.
  render(): null | React.ReactElement<React.ComponentProps<'div'>> {
    const {activeMenu, config} = this.state;
    if (activeMenu?.current && config) {
      const {x, y} = getAnchorPosition(
        activeMenu.current,
        config.placement,
        0,
        config.justify,
      );
      const style = {
        left: x,
        top: y,
      } as const;

      return (
        <div
          className={classify(css.menu, config.contextMenuClassName, {
            [css.menuDefaultStyle]: !config.noStyle,
          })}
          style={style}
          data-ui-anchor={`${config.anchorPosition || 'end'}${
            config.noArrow ? 'NoCaret' : ''
          }`}
          data-it-user-menu
          // @ts-expect-error - TS2322 - Type '(element: HTMLDivElement | null) => HTMLDivElement | null' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
          ref={(element) => (this.menuElement = element)}
          onClick={(e) => {
            // prevents context menu from closing if using render function
            if (!config.closeOnMenuClick) {
              clickedInsideMap.set(e.nativeEvent, true);
            }
          }}
        >
          {config.noStyle ? (
            config.menuContent
          ) : (
            <div className={css.items}>{config.menuContent}</div>
          )}
          {!config.noArrow && (
            <svg className={css.caret} viewBox="0 0 20 9">
              <path className={css.caretArrow} d="M0,11 L10,0.5 L20,11z" />
            </svg>
          )}
        </div>
      );
    }

    return null;
  }

  set(component: ContextMenuRef, config: ContextMenuConfig) {
    this.setState({
      activeMenu: component,
      config: {placement: 'bottom', ...config},
    });
  }

  unset(component: ContextMenuRef) {
    invariant(
      component === this.state.activeMenu,
      'Attempted to hide a menu that was not already being displayed.',
    );
    //Note (aditya) checking activeMenu to prevent flow error since activeMenu is typed ?ContextMenu and could be null
    if (this.state.activeMenu) {
      const onExit = this.state.config?.onExit;
      if (onExit) {
        onExit();
      }
    }
    this.setState({
      activeMenu: null,
      config: null,
    });
  }

  _handleWindowClick(event: MouseEvent) {
    if (clickedInsideMap.get(event)) {
      clickedInsideMap.delete(event);
      return;
    }
    // not sure we need this.
    /*
    if (this.menuElement.contains(event.target)) {
      return;
    }
    */

    const {activeMenu} = this.state;

    // NOTE (kyle): if the consumer calls `close` in a click handler, its
    // possible that the handler will bubble up after the activeMenu has been
    // removed.
    if (activeMenu) {
      if (this.state.config?.onClose) {
        this.state.config.onClose();
      }
      this.unset(activeMenu);
    }
  }
  handleWindowClick: (event: MouseEvent) => void =
    this._handleWindowClick.bind(this);
}
